import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchGET } from "../../apiHelper";
import { projektnaPomocPoDatumu } from "../../listOfApi";
import DeminimisNacrtovaneDataGrid from "./DeminimisNacrtovaneDataGrid";
import DeminimisSkrbnistvoDataGrid from "./DeminimisSkrbnistvoDataGrid";
import formatDateHelper, { formatDateTimeHelper } from "../utils/dateFormat";
import DatePicker from "react-datepicker";
import { is, sl } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

/*React-bootstrap components */
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Nav from "react-bootstrap/Nav";

/*React-icons*/
import { MdAddToPhotos } from "react-icons/md";
import { BsBarChartLine, BsPencilSquare, BsPeople } from "react-icons/bs";

import { MODULES, ROLE_GROUPS } from "../../auth/roles";
import useRoleCheck from "../../auth/useRoleCheck";
import RoleGuard from "../../auth/RoleGuard";

const DeminimisDashboard = ({ onOpenAdd, onOpenDetails }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check user roles for deminimis module
  const { isFakultetniSkrbnik, isGlobalni } = useRoleCheck(MODULES.DEMINIMIS);

  // Determine if user has access to skrbnistvo tab (both fakultetni skrbnik and globalni have access)
  const hasSkrbnistvoAccess = isFakultetniSkrbnik || isGlobalni;

  const tabOptions = useMemo(
    () => [
      {
        key: "stanje",
        label: "Stanje pridobljene pomoči (UM)",
        fullLabel: "Stanje pridobljene pomoči na ravni UM",
        icon: BsBarChartLine,
        path: "/Deminimis",
      },
      {
        key: "skrbnistvo",
        label: isFakultetniSkrbnik
          ? "Pregled in urejanje (članica)"
          : "Pregled in urejanje (celotna UM)",
        fullLabel: isFakultetniSkrbnik
          ? "Načrtovane in pridobljene pomoči za članico - prikaz in urejanje"
          : "Skrbništvo Deminimis - pregled celotne UM",
        icon: BsPencilSquare,
        path: "/Deminimis/Skrbnistvo",
      },
    ],
    [isFakultetniSkrbnik],
  );

  // Determine active tab based on current route
  const activeTab = useMemo(() => {
    const path = location.pathname;
    if (path.includes("/Skrbnistvo")) return "skrbnistvo";
    return "stanje";
  }, [location.pathname]);

  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dashboardDatumIzpisa, setDashboardDatumIzpisa] = useState(todayDate);
  const [dashboardRows, setDashboardRows] = useState([]);
  const [dashboardTotalAmount, setDashboardTotalAmount] = useState(0);
  const [isLoadingDashboardRows, setIsLoadingDashboardRows] = useState(false);
  const [dashboardRowsError, setDashboardRowsError] = useState("");

  const selectedDate = useMemo(() => {
    if (!dashboardDatumIzpisa) {
      return null;
    }

    return /^\d{4}-\d{2}-\d{2}$/.test(dashboardDatumIzpisa)
      ? dashboardDatumIzpisa
      : null;
  }, [dashboardDatumIzpisa]);

  const amountFormatter = useMemo(
    () =>
      new Intl.NumberFormat("sl-SI", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      }),
    [],
  );

  const formatAmount = (value) => amountFormatter.format(value || 0);

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "-";
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return formatDateHelper(parsedDate);
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) {
      return "-";
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return formatDateTimeHelper(parsedDate);
  };

  const selectedDateFormatted = useMemo(
    () => formatDate(selectedDate),
    [selectedDate],
  );
  const periodStartDateFormatted = useMemo(() => {
    const currentDate = new Date(selectedDate);
    if (Number.isNaN(currentDate.getTime())) {
      return "-";
    }

    const periodStartDate = new Date(currentDate);
    periodStartDate.setFullYear(periodStartDate.getFullYear() - 3);
    return formatDate(periodStartDate.toISOString().slice(0, 10));
  }, [selectedDate]);

  const visibleTabs = useMemo(() => {
    return tabOptions.filter((tab) => {
      if (tab.key === "skrbnistvo") {
        return hasSkrbnistvoAccess;
      }
      return true; // Always show "stanje" tab
    });
  }, [tabOptions, hasSkrbnistvoAccess]);

  // Redirect unauthorized users trying to access skrbnistvo tab directly
  useEffect(() => {
    if (activeTab === "skrbnistvo" && !hasSkrbnistvoAccess) {
      navigate("/Deminimis", { replace: true });
    }
  }, [activeTab, hasSkrbnistvoAccess, navigate]);

  useEffect(() => {
    if (!selectedDate) {
      setDashboardRows([]);
      setDashboardTotalAmount(0);
      setDashboardRowsError("");
      setIsLoadingDashboardRows(false);
      return;
    }

    const loadDashboardRows = async () => {
      setIsLoadingDashboardRows(true);
      setDashboardRowsError("");

      const response = await fetchGET(
        `${projektnaPomocPoDatumu}/${encodeURIComponent(selectedDate)}`,
      );

      if (response === 0 || !Array.isArray(response)) {
        setDashboardRows([]);
        setDashboardTotalAmount(0);
        setDashboardRowsError("Napaka pri nalaganju podatkov.");
        setIsLoadingDashboardRows(false);
        return;
      }

      const normalizedRows = response
        .map((item, index) => {
          const dateValue = item.datumOdobritve ?? item.DatumOdobritve ?? null;
          const provider = String(item.dajalec ?? item.Dajalec ?? "-").trim();
          const yearFromField = Number(item.leto ?? item.Leto);
          const parsedDate = dateValue ? new Date(dateValue) : null;

          const year = !Number.isNaN(yearFromField)
            ? yearFromField
            : parsedDate && !Number.isNaN(parsedDate.getTime())
              ? parsedDate.getFullYear()
              : null;

          return {
            key: `${provider || "neznano"}-${year || "brez-leta"}-${index}`,
            dajalec: provider || "-",
            leto: year,
            datumOdobritve: dateValue,
            znesek:
              Number(item.visinaOdobrenePomoci ?? item.VisinaOdobrenePomoci) ||
              0,
          };
        })
        .filter((item) => item !== null);

      const skupajRows = normalizedRows.filter(
        (row) => String(row.dajalec).trim().toUpperCase() === "SKUPAJ",
      );

      const dataRows = normalizedRows
        .filter((row) => String(row.dajalec).trim().toUpperCase() !== "SKUPAJ")
        .sort((firstRow, secondRow) => {
          const firstDate = firstRow.datumOdobritve
            ? new Date(firstRow.datumOdobritve)
            : null;
          const secondDate = secondRow.datumOdobritve
            ? new Date(secondRow.datumOdobritve)
            : null;
          const firstTimestamp =
            firstDate && !Number.isNaN(firstDate.getTime())
              ? firstDate.getTime()
              : 0;
          const secondTimestamp =
            secondDate && !Number.isNaN(secondDate.getTime())
              ? secondDate.getTime()
              : 0;

          if (firstTimestamp !== secondTimestamp) {
            return firstTimestamp - secondTimestamp;
          }

          return firstRow.dajalec.localeCompare(secondRow.dajalec, "sl-SI", {
            sensitivity: "base",
          });
        });

      const skupajAmount =
        skupajRows.length > 0
          ? skupajRows[skupajRows.length - 1].znesek
          : dataRows.reduce((sum, row) => sum + row.znesek, 0);

      setDashboardRows(dataRows);
      setDashboardTotalAmount(skupajAmount);
      setIsLoadingDashboardRows(false);
    };

    loadDashboardRows();
  }, [selectedDate]);

  return (
    <div className="deminimis-dashboard">
      <Row className="mb-3 align-items-center">
        <Col xl={8} lg={12}>
          <div className="deminimis-tabs-wrap">
            <Nav
              variant="tabs"
              activeKey={activeTab}
              onSelect={(selectedKey) => {
                const selectedTab = tabOptions.find(
                  (tab) => tab.key === selectedKey,
                );
                if (selectedTab) {
                  navigate(selectedTab.path);
                }
              }}
              className="deminimis-tabs"
            >
              {visibleTabs.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link eventKey={tab.key} title={tab.fullLabel}>
                    <tab.icon className="me-2" />
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>
        </Col>
        <Col
          xl={4}
          lg={12}
          className="deminimis-dashboard-action text-xl-end text-start mt-2 mt-xl-0"
        >
          <RoleGuard
            moduleKey={MODULES.DEMINIMIS}
            roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
          >
            <Button
              variant="outline-primary"
              className="programs-button-add-text"
              onClick={onOpenAdd}
            >
              <MdAddToPhotos className="me-2" />
              De minimis - vnos podatkov
            </Button>
          </RoleGuard>
        </Col>
      </Row>

      {activeTab === "stanje" && (
        <>
          <Row className="mb-3">
            <Col lg={4} md={6}>
              <Form.Group>
                <Form.Label>
                  Izberi datum za izpis stanja pridobljene pomoči
                </Form.Label>
                <DatePicker
                  selected={
                    dashboardDatumIzpisa ? new Date(dashboardDatumIzpisa) : null
                  }
                  onChange={(date) => {
                    if (date) {
                      setDashboardDatumIzpisa(date.toISOString().slice(0, 10));
                    } else {
                      setDashboardDatumIzpisa("");
                    }
                  }}
                  dateFormat="dd. MM. yyyy"
                  locale={sl}
                  className="form-control"
                  placeholderText="dd. mm. yyyy"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <strong>
                Prikaz pridobljene pomoči na datum: {selectedDateFormatted}.
                Upošteva se obdobje {periodStartDateFormatted} -{" "}
                {selectedDateFormatted}
              </strong>
            </Col>
          </Row>

          <Row>
            <Col lg={7}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Datum odobrene pomoči</th>
                    <th>Dajalec pomoči</th>
                    <th className="text-end">Višina odobrene pomoči €</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingDashboardRows && (
                    <tr>
                      <td colSpan={3}>Nalaganje...</td>
                    </tr>
                  )}

                  {!isLoadingDashboardRows && dashboardRowsError && (
                    <tr>
                      <td colSpan={3}>{dashboardRowsError}</td>
                    </tr>
                  )}

                  {!isLoadingDashboardRows &&
                    !dashboardRowsError &&
                    dashboardRows.length === 0 && (
                      <tr>
                        <td colSpan={3}>Ni podatkov za izbrano leto.</td>
                      </tr>
                    )}

                  {!isLoadingDashboardRows &&
                    !dashboardRowsError &&
                    dashboardRows.map((row) => (
                      <tr key={row.key}>
                        <td>{formatDate(row.datumOdobritve)}</td>
                        <td>{row.dajalec}</td>
                        <td className="text-end">{formatAmount(row.znesek)}</td>
                      </tr>
                    ))}
                  <tr>
                    <td colSpan={2}>
                      <strong>Skupna vsota:</strong>
                    </td>
                    <td className="text-end">
                      <strong>{formatAmount(dashboardTotalAmount)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        </>
      )}

      {activeTab === "skrbnistvo" && hasSkrbnistvoAccess && (
        <>
          <Row className="mb-3">
            <Col>
              <h5>
                {isFakultetniSkrbnik
                  ? "Načrtovane in odobrene pomoči po članicah"
                  : "Seznam vnosov de minimis"}
              </h5>
            </Col>
          </Row>
          <Row>
            <Col>
              {isFakultetniSkrbnik ? (
                <DeminimisNacrtovaneDataGrid onOpenDetails={onOpenDetails} />
              ) : (
                <DeminimisSkrbnistvoDataGrid onOpenDetails={onOpenDetails} />
              )}
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DeminimisDashboard;
